import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights.service';
import { UnitOfWorkService } from '../../services/unit-of-work/unit-of-work.service';
import { GlobalErrorHandler } from '../../services/global-error-handler';
import { ActivatedRoute } from '@angular/router';
import { IsBusyService } from '../../services/is-busy/is-busy.service';
import * as model from '../../../model/model';

const defaultLimit: number = 100;

@Component({
    selector: 'app-topics-and-standards',
    templateUrl: './topics-and-standards.component.html',
    standalone: false
})
export class TopicsAndStandardsComponent implements OnInit {

    @ViewChild('newTopicModal') newTopicModal: TemplateRef<any>;
    @ViewChild('newStandardModal') newStandardModal: TemplateRef<any>;

    topics: model.Topic[] = [];
    standards: model.Standard[] = [];

    page: number = 1;
    limit: number = defaultLimit;
    topicRowCount: number = 0;
    topicPageCount: number = 1;
    standardRowCount: number = 0;
    standardPageCount: number = 1;
    pageLimitOptions = [15, 25, 50, 100];
    topicNameFilter: string = null;
    standardNameFilter: string = null;
    newTopicName: string = null;
    newStandardName: string = null;
    topicIsActive: boolean = true;
    standardIsActive: boolean = true;
    editingTopic: model.Topic = null;
    editingStandard: model.Standard = null;
    topicNameDuplicate: boolean = false;
    standardNameDuplicate: boolean = false;
    isSavingTopic: boolean = false;
    isSavingStandard: boolean = false;

    constructor(
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        private activeRoute: ActivatedRoute,
        public isBusy: IsBusyService,
        private modalService: NgbModal
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.activeRoute.queryParams.subscribe(routeParams => {
            const parsedPage = parseInt(routeParams.page, 10);
            const parsedLimit = parseInt(routeParams.limit, 10);
            this.page = Number.isNaN(parsedPage) ? 1 : parsedPage;
            this.limit = Number.isNaN(parsedLimit) ? defaultLimit : parsedLimit;
            this.updateDataGrid();
        });
    }

    openNewTopic() {
        this.resetNewTopic();
        this.modalService.open(this.newTopicModal, {
            ariaLabelledBy: 'new-topic-modal-title',
            beforeDismiss: () => !this.isSavingTopic
        });
    }

    openNewStandard() {
        this.resetNewStandard();
        this.modalService.open(this.newStandardModal, {
            ariaLabelledBy: 'new-standard-modal-title',
            beforeDismiss: () => !this.isSavingStandard
        });
    }

    openEditTopic(topic: model.Topic) {
        if (!topic) {
            return;
        }

        this.editingTopic = topic;
        this.newTopicName = topic.name;
        this.topicIsActive = topic.isActive;
        this.topicNameDuplicate = false;
        this.modalService.open(this.newTopicModal, {
            ariaLabelledBy: 'new-topic-modal-title',
            beforeDismiss: () => !this.isSavingTopic
        });
    }

    openEditStandard(standard: model.Standard) {
        if (!standard) {
            return;
        }

        this.editingStandard = standard;
        this.newStandardName = standard.name;
        this.standardIsActive = standard.isActive;
        this.standardNameDuplicate = false;
        this.modalService.open(this.newStandardModal, {
            ariaLabelledBy: 'new-standard-modal-title',
            beforeDismiss: () => !this.isSavingStandard
        });
    }

    onTopicNameChanged() {
        this.topicNameDuplicate = false;
    }

    onStandardNameChanged() {
        this.standardNameDuplicate = false;
    }

    async createTopic(modal: NgbActiveModal) {
        const name = this.newTopicName ? this.newTopicName.trim() : '';
        if (!name || name.length > 200 || this.isSavingTopic) {
            return;
        }

        this.topicNameDuplicate = false;
        this.isSavingTopic = true;
        this.isBusy.set('admin:saveTopic', true, 'Saving topic');

        let topic: model.Topic = null;
        try {
            const nameExists = await this.uow.topicNameExists(name, this.editingTopic ? this.editingTopic.id : null);
            if (nameExists[0]) {
                this.topicNameDuplicate = true;
                return;
            }

            topic = this.editingTopic || this.uow.topics.createEntity();
            topic.name = name;
            topic.isActive = this.topicIsActive;

            await this.uow.topics.saveChanges([topic]);
            if (!topic.entityAspect.entityState.isUnchanged()) {
                this.uow.topics.cancelChanges(topic);
                return;
            }

            modal.close('save');
            this.resetNewTopic();
            await this.updateDataGrid();
        } catch (reason) {
            if (topic && !topic.entityAspect.entityState.isUnchanged()) {
                this.uow.topics.cancelChanges(topic);
            }
            this.errorHandler.handleError(reason);
        } finally {
            this.isSavingTopic = false;
            this.isBusy.set('admin:saveTopic', false);
        }
    }

    async createStandard(modal: NgbActiveModal) {
        const name = this.newStandardName ? this.newStandardName.trim() : '';
        if (!name || name.length > 200 || this.isSavingStandard) {
            return;
        }

        this.standardNameDuplicate = false;
        this.isSavingStandard = true;
        this.isBusy.set('admin:saveStandard', true, 'Saving standard');

        let standard: model.Standard = null;
        try {
            const nameExists = await this.uow.standardNameExists(name, this.editingStandard ? this.editingStandard.id : null);
            if (nameExists[0]) {
                this.standardNameDuplicate = true;
                return;
            }

            standard = this.editingStandard || this.uow.standards.createEntity();
            standard.name = name;
            standard.isActive = this.standardIsActive;

            await this.uow.standards.saveChanges([standard]);
            if (!standard.entityAspect.entityState.isUnchanged()) {
                this.uow.standards.cancelChanges(standard);
                return;
            }

            modal.close('save');
            this.resetNewStandard();
            await this.updateDataGrid();
        } catch (reason) {
            if (standard && !standard.entityAspect.entityState.isUnchanged()) {
                this.uow.standards.cancelChanges(standard);
            }
            this.errorHandler.handleError(reason);
        } finally {
            this.isSavingStandard = false;
            this.isBusy.set('admin:saveStandard', false);
        }
    }

    clearTopicFilters() {
        this.topicNameFilter = null;
        this.updateDataGrid();
    }

    clearStandardFilters() {
        this.standardNameFilter = null;
        this.updateDataGrid();
    }

    cancelTopic(modal: NgbActiveModal) {
        this.resetNewTopic();
        modal.dismiss('cancel');
    }

    cancelStandard(modal: NgbActiveModal) {
        this.resetNewStandard();
        modal.dismiss('cancel');
    }

    updateDataGrid() {
        this.isBusy.set('admin:loadTopicsAndStandards', true, 'Loading data');
        return Promise.all([
            this.getTopicsCount(),
            this.getTopics(),
            this.getStandardsCount(),
            this.getStandards()
        ])
            .catch(reason => {
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                this.isBusy.set('admin:loadTopicsAndStandards', false);
            });
    }

    private resetNewTopic() {
        this.newTopicName = null;
        this.topicIsActive = true;
        this.editingTopic = null;
        this.topicNameDuplicate = false;
    }

    private resetNewStandard() {
        this.newStandardName = null;
        this.standardIsActive = true;
        this.editingStandard = null;
        this.standardNameDuplicate = false;
    }

    getTopics() {
        return this.uow.getTopics(this.topicNameFilter, this.page, this.limit)
            .then(result => {
                this.topics = result;
            });
    }

    getTopicsCount() {
        return this.uow.getTopicsCount(this.topicNameFilter)
            .then(result => {
                this.topicRowCount = result[0];
                this.topicPageCount = Math.ceil(this.topicRowCount / this.limit);
                if (this.topicPageCount < 1) {
                    this.topicPageCount = 1;
                }
            });
    }

    getStandards() {
        return this.uow.getStandards(this.standardNameFilter, this.page, this.limit)
            .then(result => {
                this.standards = result;
            });
    }

    getStandardsCount() {
        return this.uow.getStandardsCount(this.standardNameFilter)
            .then(result => {
                this.standardRowCount = result[0];
                this.standardPageCount = Math.ceil(this.standardRowCount / this.limit);
                if (this.standardPageCount < 1) {
                    this.standardPageCount = 1;
                }
            });
    }

    getTopicLastModifiedDate(topic: model.Topic) {
        return topic.modifiedDate || topic.createdDate;
    }

    getTopicLastModifiedBy(topic: model.Topic) {
        return this.getEmployeeDisplayName(topic.modifiedByUser || topic.createdByUser);
    }

    getStandardLastModifiedDate(standard: model.Standard) {
        return standard.modifiedDate || standard.createdDate;
    }

    getStandardLastModifiedBy(standard: model.Standard) {
        return this.getEmployeeDisplayName(standard.modifiedByUser || standard.createdByUser);
    }

    private getEmployeeDisplayName(employee: model.Employee) {
        if (!employee) {
            return '---';
        }

        if (employee.fullName && employee.fullName.trim() !== '') {
            return employee.fullName;
        }

        const firstName = employee.firstName ? employee.firstName : '';
        const lastName = employee.lastName ? employee.lastName : '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName !== '' ? fullName : employee.id;
    }
}
